import React, { useEffect, useRef, useState } from "react";
import { motion, useAnimate } from "motion/react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { gsap } from "gsap"
import { Flip } from "gsap/Flip"

gsap.registerPlugin(Flip);

const ProjectTile = ({
  i,
  workLength,
  workRef,
  data,
  workPos,
  vW,
  vH,
  mainPortfolio,
  handleSwitchPortfolio,
  expandedRef,
  portfolioSwitchQueued,
  portfolioSwitchPendingRef
}) => {

  const navigate = useNavigate();
  const location = useLocation();
  const [scope, animate] = useAnimate();
  const [expanded, setExpanded] = useState(false);

  const prevPos = useRef(workPos);
  const imgRef = useRef(null);
  const imgContainerRef = useRef(null);

  useEffect(() => {
    if (!portfolioSwitchQueued && !portfolioSwitchPendingRef.current) return;

    gsap.killTweensOf('.project-image, .project-image-sub, .project-image-sub2, .project-info, .project-tile');
    const tl = gsap.timeline({
      defaults: { overwrite: "auto" }
    });
    
    if (!portfolioSwitchQueued && portfolioSwitchPendingRef.current) {
      //bring back
      gsap.set(".thumb-img", {rotateY: -85, opacity: 0});
      gsap.set(".project-info", {opacity: 0});
      gsap.set(".project-tile", {scale: 0.95});

      tl.to('.project-image', {rotateY: 0, opacity: 1, duration: 2, ease: "elastic.out(1.4, 1)"})
      .to('.project-image-sub', { rotateY: 0, opacity: 1, duration: 1, ease: "elastic.out(1.4, 1)"}, "<")
      .to('.project-info', { opacity: 1, duration: 0.5, ease: "power1.inOut"}, "<")
      .to('.project-image-sub2', { rotateY: 0, opacity: 1, duration: 2.5, ease: "elastic.out(1.4, 1)"}, "<+0.2")
      .to(".project-tile", {scale: 1, duration: 0.3, onComplete: () => portfolioSwitchPendingRef.current = false}, "<+0.7");
    }
    else {
      //start transition
      gsap.set(".thumb-img", {rotateY: 0, opacity: 1});
      gsap.set(".project-info", {opacity: 1});
      gsap.set(".project-tile", {scale: 1});
      
      tl.to(".project-tile", {scale: 0.95, duration: 0.3})
      .to('.project-image, .project-image-sub, .project-image-sub2', {
        rotateY: -85,
        opacity: 0,
        duration: 0.5,
        ease: "power1.inOut"
      }, '<')
      .to('.project-info', {
        opacity: 0,
        duration: 0.5,
        ease: "power1.inOut",
        onComplete: () => {
          portfolioSwitchPendingRef.current = true;
          handleSwitchPortfolio();
        }
      }, '<');
    }
  }, [portfolioSwitchQueued]);

  useEffect(() => {

    if (portfolioSwitchQueued || portfolioSwitchPendingRef.current) return;

    const isLeft = prevPos.current > workPos;

    gsap.killTweensOf('.project-tile:not(.off) .thumb-img');
    gsap.set(".project-tile:not(.off) .thumb-img", { rotateY: 0, opacity: 1});

    const tl = gsap.timeline({
      defaults: { overwrite: "auto" }
    });

    // Quick rotate
    tl.to(".project-tile:not(.off) .thumb-img", {
      rotateY: isLeft ? -16 : 16,
      duration: 0.1,
      ease: "power1.out"
    }, 0);

    // Spring back to 0
    tl.to(".project-tile:not(.off) .thumb-img", {
      rotateY: 0,
      duration: 6,
      ease: "elastic.out(1.4, 0.35)"
    });
    prevPos.current = workPos;
  }, [workPos, vW]);

  useEffect(() => {
    if (expanded && location.pathname === '/work') expand();
  }, [location]);

  const openFocus = () => {
    expand();
    navigate(`/${data.slug}`);
  }

  const expand = () => {
    const state = Flip.getState(imgRef.current);

    if (!expanded) {

      const placeholder = document.createElement('div');
      placeholder.className = 'placeholder-project-image';

      imgContainerRef.current.appendChild(placeholder);
      expandedRef.current.appendChild(imgRef.current);

      gsap.to(".project-images", {height: '100vh', duration: 2, ease: 'power2.out'});
      gsap.to(".project-head, .project-info, .project-image-sub, .project-image-sub2", {opacity: 0, duration: 0.5, ease: 'power2.out'});

      imgRef.current.style.rotateZ = "0deg";

      Flip.from(state, {
        duration: 0.8,
        ease: "circ.in",
        absolute: true,
        scale: false,
        onStart: () => {
          const scrollY = window.scrollY;

          document.body.style.position = "fixed";
          document.body.style.top = `-${scrollY}px`;
          document.body.style.left = "0";
          document.body.style.right = "0";
        },
        onComplete: () => {
          gsap.set(imgRef.current, {clearProps: "transform"});
          gsap.set(imgRef.current, {opacity: 0, delay: 1});
        }
      });
    } else {
      
      gsap.killTweensOf(imgRef.current);
      gsap.set(imgRef.current, {opacity: 1});

      gsap.to(".project-images", {height: 'auto', duration: 1.5, ease: 'power2.out'});
      gsap.to(".project-head, .project-info, .project-image-sub, .project-image-sub2", {opacity: 1, duration: 0.7, delay: 0.2, ease: "power2.in"});

      //move image back to original container
      imgContainerRef.current.prepend(imgRef.current);
      imgRef.current.style.rotateZ = "2deg";

      Flip.from(state, {
        duration: 1,
        ease: 'power3.inOut',
        absolute: true,
        scale: false,
        onComplete: () => {

          //remove placeholder
          const el = imgContainerRef.current.querySelector('.placeholder-project-image');
          if (el) el.remove();

          const scrollY = -parseInt(document.body.style.top || "0");

          document.body.style.position = "";
          document.body.style.top = "";
          document.body.style.left = "";
          document.body.style.right = "";

          window.scrollTo(0, scrollY);
        }
      });
    }

    setExpanded(!expanded);
  }

  return (
    <motion.div
      ref={scope}
      className={(workPos === (i + 1)) ? ("project-tile") : ("project-tile off")}
    >
      <div onClick={openFocus} className="project-images">
        <div
          ref={imgContainerRef}
        >
          <div
            ref={imgRef}
            className="project-image thumb-img"
            role={"button"}
          >
            <img src={`img/projects/${data.slug}/thumb.jpg`} />
          </div>
        </div>
        <div className="project-image-sub thumb-img">
          <img src={`img/projects/${data.slug}/thumb2.jpg`} />
        </div>
        <div className="project-image-sub2 thumb-img">
          <img src={`img/projects/${data.slug}/thumb3.jpg`} />
        </div>
      </div>
      <div className="project-info">
        <aside className="project-meta">
          <div className="tags">
            {data.tags.map((tag, i) => <h4 key={i}>{tag}</h4>)}
          </div>
          <h1 onClick={openFocus}>{data.title}</h1>
          <h3 className="date">{`(${data.year})`}</h3>
        </aside>
        <div className="extra-info">
          <h4>{i + 1}/{workLength}</h4>
          <h4>{data.status}</h4>
        </div>
        <p className="desc">{data.desc}</p>
        <div className="expand-project" role={"button"}>
          <h3 onClick={openFocus}>{`View Project ${String.fromCharCode(8594)}`}</h3>
        </div>
      </div>
    </motion.div>
  )
}

export default ProjectTile;